import moment from 'moment';
import { IntervalReport } from 'api/intervals-report';
import { Field } from 'api/tasks';
import { getChartOptions } from '../chart-utils';
import { ChartZoomLevel } from '../page';
import { initialAverageAndDeviation } from '../types';
import { findAllMonthLabels, findWeeklyLabels } from '../utils';

interface IntervalData extends Omit<IntervalReport, 'intervalId'> {
  intervalId: string | number;
}

const processMonthlyData = (monthlyIntervalData, intervals) => {
  let dates: number[] = [];
  const intervalWiseData =
    (monthlyIntervalData ?? [])?.reduce(
      (acc, interval) => {
        if (interval.values.length === 0) {
          return acc;
        }
        const toReturn = interval.values.reduce(
          (acc1, curr) => {
            const currDate = Number(
              `${curr.year}${
                curr.month < 10 ? String(`0${curr.month}`) : curr.month
              }`,
            );

            if (!dates.includes(currDate)) {
              dates = [...dates, currDate];
            }

            return {
              ...acc1,
              average: {
                ...acc1.average,
                [currDate]: {
                  y: curr.value,
                  noOfProjects: curr.projectIds.length,
                  growth: curr.growth ?? null,
                  name:
                    intervals[interval.intervalId]?.fieldLabel ??
                    intervals[interval.intervalId],
                },
              },
              deviation: {
                ...acc1.deviation,
                [currDate]: {
                  y: curr.deviation,
                  noOfProjects: curr.projectIds.length,
                  growth: curr.growth ?? null,
                  name:
                    intervals[interval.intervalId]?.fieldLabel ??
                    intervals[interval.intervalId],
                },
              },
            };
          },
          { average: {}, deviation: {} },
        );

        return {
          intervalData: {
            ...acc.intervalData,
            [interval.intervalId]: toReturn,
          },
        };
      },
      { intervalData: {} },
    ) ?? {};

  dates.sort();

  const monthLabels = findAllMonthLabels(dates[0], dates[dates.length - 1]);
  const xAxisDates: string[] = Object.keys(monthLabels).sort(function (a, b) {
    return Number(a) - Number(b);
  });
  const xAxisLabels = xAxisDates.map((index) => monthLabels[index]);

  const seriesData = Object.keys(intervalWiseData.intervalData).reduce(
    (acc, id) => {
      const { average, deviation } = intervalWiseData.intervalData[id];
      const averageData = xAxisDates.map((date) =>
        average[date] ? average[date] : {},
      );
      const deviationData = xAxisDates.map((date) =>
        deviation[date] ? deviation[date] : {},
      );

      return {
        ...acc,
        average: [
          ...acc.average,
          {
            name: intervals[id]?.fieldLabel ?? intervals[id],
            data: averageData,
          },
        ],
        deviation: [
          ...acc.deviation,
          {
            name: intervals[id]?.fieldLabel ?? intervals[id],
            data: deviationData,
          },
        ],
      };
    },
    initialAverageAndDeviation,
  );

  return { xAxisLabels, seriesData };
};

const processWeeklyData = (weeklyIntervalData, intervals) => {
  let weekDates: number[] = [];
  const weekWiseIntervalData =
    (weeklyIntervalData ?? [])?.reduce((acc, interval) => {
      if (interval.values.length === 0) {
        return acc;
      }
      const toReturn = interval.values.reduce(
        (acc1, curr) => {
          const currWeek =
            curr.month === 12 && curr.weekOfMonth === 1
              ? Number(
                  `${moment(String(curr.year)).add(1, 'y').format('YYYY')}0101`,
                )
              : Number(
                  `${curr.year}${
                    curr.month < 10 ? String(`0${curr.month}`) : curr.month
                  }${
                    curr.weekOfMonth
                      ? curr.weekOfMonth < 10
                        ? String(`0${curr.weekOfMonth}`)
                        : curr.weekOfMonth
                      : ''
                  }`,
                );

          if (!weekDates.includes(currWeek)) {
            weekDates = [...weekDates, currWeek];
          }

          const weekStartDate = moment(String(currWeek).slice(0, 4))
            .week(Number(String(currWeek).slice(6, 8)))
            .startOf('week');

          return {
            ...acc1,
            average: {
              ...acc1.average,
              [Number(
                `${weekStartDate.format('YY')}${weekStartDate.format(
                  'MM',
                )}${weekStartDate.format('DD')}`,
              )]: {
                y: curr.value,
                noOfProjects: curr.projectIds.length,
                growth: curr.growth ?? null,
                name:
                  intervals[interval.intervalId]?.fieldLabel ??
                  intervals[interval.intervalId],
              },
            },
            deviation: {
              ...acc1.deviation,
              [Number(
                `${weekStartDate.format('YY')}${weekStartDate.format(
                  'MM',
                )}${weekStartDate.format('DD')}`,
              )]: {
                y: curr.deviation,
                noOfProjects: curr.projectIds.length,
                growth: curr.growth ?? null,
                name:
                  intervals[interval.intervalId]?.fieldLabel ??
                  intervals[interval.intervalId],
              },
            },
          };
        },
        { average: {}, deviation: {} },
      );

      return { ...acc, [interval.intervalId]: toReturn };
    }, {}) ?? {};

  weekDates.sort();
  const weeklyMonthLabels =
    findWeeklyLabels(weekDates[0], weekDates[weekDates.length - 1]) ?? {};
  const weeklyxAxisDates: string[] = Object.keys(weeklyMonthLabels).sort(
    function (a, b) {
      return Number(a) - Number(b);
    },
  );
  const xAxisLabels = weeklyxAxisDates.map((index) => weeklyMonthLabels[index]);

  const seriesData = Object.keys(weekWiseIntervalData).reduce((acc, id) => {
    const { average, deviation } = weekWiseIntervalData[id];
    const averageData = weeklyxAxisDates.map((date) => average[date] ?? {});
    const deviationData = weeklyxAxisDates.map((date) => deviation[date] ?? {});

    return {
      ...acc,
      average: [
        ...acc.average,
        {
          name: intervals[id]?.fieldLabel ?? intervals[id],
          data: averageData,
        },
      ],
      deviation: [
        ...acc.deviation,
        {
          name: intervals[id]?.fieldLabel ?? intervals[id],
          data: deviationData,
        },
      ],
    };
  }, initialAverageAndDeviation);

  return { xAxisLabels, seriesData };
};

const getProcessedData = (intervalData, intervals, zoomLevel) => {
  if (zoomLevel === 'monthly') {
    return processMonthlyData(intervalData, intervals);
  }
  if (zoomLevel === 'weekly') {
    return processWeeklyData(intervalData, intervals);
  }

  return { xAxisLabels: [], seriesData: { average: [], deviation: [] } };
};

export const computeChartData = (
  intervalData: IntervalData[],
  zoomLevel: ChartZoomLevel,
  intervals: Record<number, Field>,
  translate: any,
) => {
  const { xAxisLabels, seriesData } = getProcessedData(
    intervalData,
    intervals,
    zoomLevel,
  );

  const absoluteValueChartOptions = getChartOptions(
    xAxisLabels,
    seriesData.average,
    translate('reports.intervals_overview.absolute_chart.xAxis'),
    translate('reports.intervals_overview.absolute_chart.yAxis'),
  );

  const deviationChartOptions = getChartOptions(
    xAxisLabels,
    seriesData.deviation,
    translate('reports.intervals_overview.deviation_chart.xAxis'),
    translate('reports.intervals_overview.deviation_chart.yAxis'),
  );

  return { absoluteValueChartOptions, deviationChartOptions };
};
